/**
 * Turns a (flat) JSON Schema object into a list of form fields the Playground
 * can render as labelled inputs, and rebuilds a typed request object from the
 * string values those inputs hold. Only the subset the AnyAPI SKUs use is
 * supported: top-level object, scalar properties (string/number/integer/
 * boolean), `enum`, `default`, `description`, and numeric `minimum`/`maximum`.
 */

type FieldKind = 'string' | 'integer' | 'number' | 'boolean' | 'enum' | 'array';

export interface SchemaField {
  name: string;
  kind: FieldKind;
  required: boolean;
  description?: string;
  default?: string;
  /** A known-valid example value for this field, used to prefill the Playground
   *  so a first "Run" succeeds out of the box. */
  example?: string;
  /** allowed values when kind === 'enum' */
  options?: string[];
  /** allowed values for each ELEMENT when kind === 'array' and the schema
   *  constrains `items.enum`, so the form can offer a checkbox per value
   *  instead of a free-text list. */
  itemOptions?: string[];
  /** Enum values serialize as numbers when the schema's canonical type is numeric. */
  numericEnum?: boolean;
  min?: number;
  max?: number;
}

interface RawProp {
  type?: string | string[];
  description?: string;
  enum?: unknown[];
  default?: unknown;
  example?: unknown;
  minimum?: number;
  maximum?: number;
  items?: { enum?: unknown[] };
}

interface RawSchema {
  properties?: Record<string, RawProp>;
  required?: string[];
  /** A whole-input example object, e.g. `{ "handle": "nasa" }`. Preferred over
   *  per-field examples when present. */
  example?: Record<string, unknown>;
}

const asObject = (v: unknown): Record<string, unknown> | null =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;

/** Renders a schema value as the string a form input holds: objects/arrays as
 *  compact JSON, scalars verbatim, null/undefined as blank. */
const toFieldString = (v: unknown): string =>
  v === null || v === undefined
    ? ''
    : typeof v === 'object'
      ? JSON.stringify(v)
      : String(v);

/** The whole-input example object declared at the schema root, if any. */
export const schemaExample = (schema: unknown): Record<string, unknown> | null => {
  const root = asObject(schema) as RawSchema | null;
  const ex = root?.example;
  return ex && typeof ex === 'object' && !Array.isArray(ex) ? ex : null;
};

const kindOf = (p: RawProp): FieldKind => {
  if (Array.isArray(p.enum) && p.enum.length > 0) return 'enum';
  const t = Array.isArray(p.type) ? p.type[0] : p.type;
  if (t === 'integer') return 'integer';
  if (t === 'number') return 'number';
  if (t === 'boolean') return 'boolean';
  if (t === 'array') return 'array';
  return 'string';
};

/** The element enum of an array property, e.g. `requireFields`'s field names. */
const itemEnum = (p: RawProp): string[] | undefined => {
  const values = p.items?.enum;
  return Array.isArray(values) && values.length > 0
    ? [...new Set(values.map(String))]
    : undefined;
};

const canonicalType = (p: RawProp): string | undefined =>
  Array.isArray(p.type) ? p.type[0] : p.type;

/**
 * Gateway routing directives the server injects into EVERY SKU's published
 * input schema: the latency preference and the three that name sources
 * (`source`, `allowFallbacks`, `ignoreSources`). They steer routing and are
 * forwarded to no source, so rendering one would put a form field a caller
 * cannot usefully fill from a form on all 359 endpoint pages. The authored
 * capability directives (`requireCursor`, `requirePlayCount`,
 * `requireSinglePage`) stay visible: they are declared only on the handful of
 * SKUs where they steer, and are useful toggles there.
 *
 * This is a deliberate second copy of the `domain.InjectedDirectives` field names
 * (server/internal/domain/routing_directive_schema.go); the two cannot import
 * each other. `scripts/check-injected-directives.sh` fails CI when they differ,
 * because a duplicated input-field set here has drifted once before. That guard
 * greps for the exported declaration, so the export stays even with no importer.
 *
 * @public
 */
export const INJECTED_DIRECTIVE_FIELDS = ['preferLatencyUnderMs', 'source', 'allowFallbacks', 'ignoreSources'];

/**
 * Returns an input schema with the injected routing directives removed, so any
 * consumer that walks `properties` shows only what a caller sends to the source.
 *
 * Every surface that renders an INPUT schema must go through this. Two do it by
 * calling `parseSchema` (which strips internally); the sell page's sample
 * request body walks `properties` itself via `sampleFromSchema`, which is how it
 * came to advertise a body field the gateway consumes and forwards to no lane.
 * Property order is preserved, and the returned object is a shallow copy - the
 * cached query data it is derived from is never mutated.
 */
export const withoutInjectedDirectives = (schema: unknown): unknown => {
  const root = asObject(schema);
  const props = root ? asObject(root.properties) : null;
  if (!root || !props) return schema;
  const hidden = new Set<string>(INJECTED_DIRECTIVE_FIELDS);
  const kept = Object.entries(props).filter(([name]) => !hidden.has(name));
  if (kept.length === Object.keys(props).length) return schema;
  return { ...root, properties: Object.fromEntries(kept) };
};

/** Parses a SKU input schema into ordered form fields (required ones first). */
export const parseSchema = (schema: unknown): SchemaField[] => {
  const root = asObject(withoutInjectedDirectives(schema)) as RawSchema | null;
  if (!root?.properties) return [];
  const required = new Set(root.required ?? []);

  const fields = Object.entries(root.properties).map(([name, raw]): SchemaField => {
    const p = (asObject(raw) as RawProp) ?? {};
    return {
      name,
      kind: kindOf(p),
      required: required.has(name),
      description: p.description,
      default: p.default !== undefined ? String(p.default) : undefined,
      example: p.example !== undefined ? toFieldString(p.example) : undefined,
      options: Array.isArray(p.enum) ? [...new Set(p.enum.map(String))] : undefined,
      itemOptions: itemEnum(p),
      numericEnum:
        Array.isArray(p.enum) &&
        (canonicalType(p) === 'integer' || canonicalType(p) === 'number'),
      min: p.minimum,
      max: p.maximum,
    };
  });

  // Required fields first, then original order - the common path is on top.
  return fields.sort((a, b) => Number(b.required) - Number(a.required));
};

/**
 * The initial string value for each field, in priority order: the schema's
 * whole-input `example` (so a first "Run" works), then the field's own example,
 * then its default, then - for a required field only - the first enum option or
 * `false`, then a blank. An optional field stays blank so the request omits it
 * and the gateway applies its own default: seeding google.search's `timeframe`
 * with its first option sent every playground search as "past hour only".
 * Pass the root example via `example` (see `schemaExample`).
 */
export const initialValues = (
  fields: SchemaField[],
  example?: Record<string, unknown> | null,
): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const f of fields) {
    if (example && example[f.name] !== undefined) out[f.name] = toFieldString(example[f.name]);
    else if (f.example !== undefined) out[f.name] = f.example;
    else if (f.default !== undefined) out[f.name] = f.default;
    else if (f.required && f.kind === 'enum' && f.options?.length) out[f.name] = f.options[0];
    else if (f.required && f.kind === 'boolean') out[f.name] = 'false';
    else out[f.name] = '';
  }
  return out;
};

/** Names of required fields the user left blank (for inline validation). */
export const missingRequired = (fields: SchemaField[], values: Record<string, string>): string[] =>
  fields.filter((f) => f.required && (values[f.name] ?? '').trim() === '').map((f) => f.name);

/**
 * Builds the typed `input` object from the form's string values: numbers are
 * coerced, booleans parsed, and blank optional fields dropped so the request
 * stays minimal. Blank required fields are included as "" so the gateway's
 * validation error points at them.
 */
export const buildInput = (
  fields: SchemaField[],
  values: Record<string, string>,
): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    const raw = (values[f.name] ?? '').trim();
    if (raw === '' && !f.required) continue;
    if (raw === '') {
      out[f.name] = '';
      continue;
    }
    if (f.kind === 'integer' || f.kind === 'number' || f.numericEnum) {
      const n = Number(raw);
      out[f.name] = Number.isFinite(n) ? n : raw;
    } else if (f.kind === 'boolean') {
      out[f.name] = raw === 'true';
    } else if (f.kind === 'array') {
      out[f.name] = parseArrayInput(raw);
    } else {
      out[f.name] = raw;
    }
  }
  return out;
};

/**
 * Parses an array field's text into a JSON array so the gateway receives an
 * actual array (not the raw string). Accepts JSON array text (`["a","b"]`) or a
 * comma/newline-separated list (`a, b`); the latter yields a string array. A
 * value that is neither a JSON array nor splittable round-trips as a single-
 * element array so a lone entry still sends as `["a"]`.
 */
const parseArrayInput = (raw: string): unknown => {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
  } catch {
    // not JSON - fall through to delimiter split
  }
  return raw
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter((s) => s !== '');
};
